import React from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const testimonials = [
  {
    id: 1,
    name: 'Sarah Chen',
    rating: 5,
    date: '2 weeks ago',
    text: 'Excellent service! My car looks brand new after the collision repair. The team was professional, kept me updated throughout the process, and finished ahead of schedule. Highly recommend!',
    service: 'Collision Repair',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop'
  },
  {
    id: 2,
    name: 'Michael Johnson',
    rating: 5,
    date: '1 month ago',
    text: 'The paint job on my truck is flawless. Perfect color match and the attention to detail is outstanding. Fair pricing and honest service. Will definitely come back for any future needs.',
    service: 'Auto Painting',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop'
  },
  {
    id: 3,
    name: 'Jennifer Lee',
    rating: 5,
    date: '1 month ago',
    text: 'Amazing dent removal work! They removed several dents from my car door and you cannot tell there was ever any damage. Professional team and great communication.',
    service: 'Dent Removal',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop'
  },
  {
    id: 4,
    name: 'David Park',
    rating: 5,
    date: '2 months ago',
    text: 'Best auto body shop in Burnaby! They worked with my insurance company directly and made the whole process stress-free. Quality work and reasonable turnaround time.',
    service: 'Collision Repair',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop'
  },
  {
    id: 5,
    name: 'Lisa Thompson',
    rating: 5,
    date: '2 months ago',
    text: 'Professional service from start to finish. They explained everything clearly, provided a detailed estimate, and the final result exceeded my expectations. Highly trustworthy!',
    service: 'Auto Painting',
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop'
  },
  {
    id: 6,
    name: 'Robert Kim',
    rating: 5,
    date: '3 months ago',
    text: 'Fantastic experience! Quick turnaround, excellent workmanship, and very friendly staff. My car looks better than it did before the accident. Thank you Kingsway Auto Collision!',
    service: 'Collision Repair',
    image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop'
  }
];

export const Testimonials = () => {
  const averageRating = 5.0;
  const totalReviews = testimonials.length;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-8 w-8 fill-amber-500 text-amber-500" />
            ))}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            What Our Customers Say
          </h2>
          <p className="text-xl text-slate-600 mb-2">
            {averageRating.toFixed(1)} out of 5 stars
          </p>
          <p className="text-lg text-slate-500">
            Based on {totalReviews}+ verified customer reviews
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <Card 
              key={testimonial.id}
              className="relative overflow-hidden hover:shadow-xl transition-all duration-300 border-slate-200"
            >
              <CardContent className="p-6">
                {/* Quote Icon */}
                <div className="absolute top-4 right-4 opacity-10">
                  <Quote className="h-12 w-12 text-slate-900" />
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-amber-500 text-amber-500" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>

                {/* Customer Info */}
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.service}</p>
                    <p className="text-xs text-slate-400">{testimonial.date}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Google Reviews CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">
            See more reviews on Google
          </p>
          <a
            href="https://www.google.com/search?q=kingsway+auto+collision+burnaby+reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 font-semibold transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            View Google Reviews
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
